import { EventType, JoinRule, KnownMembership, RestrictedAllowType, RoomMember } from 'matrix-js-sdk/src/matrix';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { syncMeetingFromRoom } from 'api/meedioBackend/syncMeetingFromRoom';
import { ReactComponent as Alert } from 'assets/icons/Alert.svg';
import useMatrixContext from 'contexts/MatrixContext/useMatrixContext';
import useToast from 'contexts/ToastProvider/useToast';
import { RoomAccessType } from 'hooks/roomCreation/useRoomAccessInitialState';
import logger from 'utils/logger';
import { getCanonicalParentRoom, hasWorkspaceMeta } from 'utils/roomHierarchyUtils';

type ToggleWaitingListProps = {
  roomId: string;
  isWaitingListEnabled: boolean;
  waitingListMembers?: RoomMember[];
  workspaceId?: string | null;
  /** Workspace rooms: toggle via eu.meedio.room.access (team ↔ knock); bot owns join_rules. */
  changeRoomAccess?: (accessType: Extract<RoomAccessType, 'team' | 'knock'>) => Promise<void>;
};

const useToggleWaitingList = () => {
  const { matrixClient } = useMatrixContext();
  const [isToggleWaitingListLoading, setIsToggleWaitingListLoading] = useState(false);
  const { pushToast } = useToast();
  const { t } = useTranslation();

  const inviteAllWaitingListMembers = (roomId: string, waitingListMembers?: RoomMember[]) => {
    let members = waitingListMembers;
    if (!members) {
      const room = matrixClient.getRoom(roomId);
      members = room?.getMembers().filter(({ membership }) => membership === KnownMembership.Knock) || [];

      if (!members.length) return;
    }

    return Promise.all(
      members.map(({ userId, rawDisplayName }) =>
        matrixClient.invite(roomId, userId).catch((err) => {
          logger.error(`Could not invite user ${userId} to room ${roomId}`, err);
          pushToast({
            title: t('something_wrong'),
            description: t('could_not_invite_user_to_room', { displayName: rawDisplayName }),
            icon: Alert,
            variant: 'warning',
          });
        })
      )
    );
  };

  const renderErrorToast = () =>
    pushToast({
      title: t('something_wrong'),
      description: t('could_not_toggle_waiting_list'),
      icon: Alert,
      variant: 'warning',
    });

  const getDisableWaitingListJoinRuleContent = (workspaceId?: string | null) =>
    workspaceId
      ? { join_rule: JoinRule.Restricted, allow: [{ type: RestrictedAllowType.RoomMembership, room_id: workspaceId }] }
      : { join_rule: JoinRule.Public };

  const changeJoinRule = (joinRule: JoinRule.Public | JoinRule.Knock, roomId: string) =>
    matrixClient.sendStateEvent(roomId, EventType.RoomJoinRules, { join_rule: joinRule }).catch((error) => {
      logger.error(error);
      renderErrorToast();
    });

  const resolveWorkspaceId = (roomId: string, explicitWorkspaceId?: string | null): string | null => {
    if (explicitWorkspaceId !== undefined) return explicitWorkspaceId;
    const room = matrixClient.getRoom(roomId);
    if (!room) return null;
    const parent = getCanonicalParentRoom(room);
    if (!parent || !hasWorkspaceMeta(parent)) return null;
    return parent.roomId;
  };

  const toggleWaitingList = ({
    roomId,
    isWaitingListEnabled,
    waitingListMembers,
    workspaceId,
    changeRoomAccess,
  }: ToggleWaitingListProps) => {
    if (!roomId) return;

    setIsToggleWaitingListLoading(true);

    const newEnabled = !isWaitingListEnabled;
    const resolvedWorkspaceId = resolveWorkspaceId(roomId, workspaceId);

    const accessTogglePromise = changeRoomAccess
      ? changeRoomAccess(newEnabled ? 'knock' : 'team').catch((error) => {
          logger.error(error);
          renderErrorToast();
        })
      : null;

    const joinRulePromise =
      changeRoomAccess === undefined
        ? isWaitingListEnabled
          ? matrixClient
              .sendStateEvent(
                roomId,
                EventType.RoomJoinRules,
                getDisableWaitingListJoinRuleContent(resolvedWorkspaceId)
              )
              .catch((error) => {
                logger.error(error);
                renderErrorToast();
              })
          : changeJoinRule(JoinRule.Knock, roomId)
        : accessTogglePromise;

    return joinRulePromise
      ?.then(() => {
        if (isWaitingListEnabled) inviteAllWaitingListMembers(roomId, waitingListMembers);
        return syncMeetingFromRoom(roomId, { waiting_list_enabled: newEnabled });
      })
      .finally(() => setIsToggleWaitingListLoading(false));
  };

  return { toggleWaitingList, isToggleWaitingListLoading };
};

export default useToggleWaitingList;
